import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCcw, Wallet, ExternalLink,
  Search, ChevronDown, ChevronRight, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const eur = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return `€${v.toFixed(2)}`;
};

const TABS = [
  { title: "TJEDNI_UNOS 13%+5%", model: "13_5", label: "13% + 5%" },
  { title: "TJEDNI_UNOS 20%+5%", model: "20_5", label: "20% + 5%" },
  { title: "TJEDNI_UNOS 6535",   model: "65_35", label: "65 / 35" },
];

const SHEET_URL_TMPL = (sid) =>
  `https://docs.google.com/spreadsheets/d/${sid}/edit`;

// ----- Column groups for the detail row -----
const UBER_COLS = [
  ["uber_bruto",   "Bruto"],
  ["uber_neto",    "Neto"],
  ["uber_cash",    "Cash"],
  ["uber_bonuses", "Bonuses"],
  ["uber_tips",    "Tips"],
  ["uber_fees",    "Fees"],
];
const BOLT_COLS = [
  ["bolt_bruto",   "Bruto"],
  ["bolt_neto",    "Neto"],
  ["bolt_cash",    "Cash"],
  ["bolt_bonuses", "Bonuses"],
  ["bolt_tips",    "Tips"],
  ["bolt_fees",    "Fees"],
];

export default function AdminPayouts() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("TJEDNI_UNOS 13%+5%");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [period, setPeriod] = useState("");
  const [expanded, setExpanded] = useState({});

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payouts/sheets/status");
      setStatus(data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin");
    }
  }, [navigate]);

  const loadTab = useCallback(async (tab) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts/sheets/payouts", {
        params: { tab },
      });
      const first = (data?.tabs || [])[0];
      setRows(first?.rows || []);
    } catch (err) {
      if (err?.response?.status === 401) return navigate("/admin");
      toast.error(err?.response?.data?.detail || "Failed to load payouts from Sheets.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/payouts/sheets/reload");
      await loadTab(activeTab);
      toast.success("Refreshed from Google Sheets.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  }, [activeTab, loadTab]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadTab(activeTab); }, [activeTab, loadTab]);

  // ----- Derived: unique periods for the period filter -----
  const periods = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.period) set.add(r.period); });
    return [...set];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (period && r.period !== period) return false;
      if (!q) return true;
      return (r.driver_name || "").toLowerCase().includes(q) ||
             (r.phone || "").toLowerCase().includes(q);
    });
  }, [rows, query, period]);

  const totals = useMemo(() => {
    const acc = {
      total_turnover: 0, gross_turnover: 0, vat: 0,
      company_commission: 0, contributions: 0,
      total_cash: 0, brought_cash: 0, final_payout: 0,
    };
    filtered.forEach((r) => {
      Object.keys(acc).forEach((k) => { acc[k] += Number(r[k] || 0); });
    });
    return acc;
  }, [filtered]);

  const toggle = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));

  return (
    <div data-testid="admin-payouts-page" className="min-h-screen bg-[#050505] text-zinc-100">
      {/* Header */}
      <div className="border-b border-white/5 bg-black/60 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center gap-4">
          <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white transition" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Wallet className="w-5 h-5 text-[#C5A059]" />
              <h1 className="text-xl font-light tracking-wide">Payouts</h1>
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border border-white/10 px-2 py-0.5 rounded-full">
                Source: Google Sheets
              </span>
            </div>
            {status?.spreadsheet_id && (
              <a
                href={SHEET_URL_TMPL(status.spreadsheet_id)}
                target="_blank" rel="noreferrer"
                className="text-xs text-zinc-500 hover:text-[#C5A059] inline-flex items-center gap-1 mt-1"
                data-testid="open-sheet-link"
              >
                Open master workbook <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <button
            onClick={refresh}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-sm hover:bg-[#C5A059]/10 disabled:opacity-40 transition"
            data-testid="refresh-from-sheets-btn"
          >
            <RefreshCcw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh from Sheets"}
          </button>
          <Link
            to="/admin/self-billing"
            data-testid="nav-self-billing"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-zinc-300 text-sm hover:border-[#C5A059] hover:text-[#C5A059] transition ml-2"
          >
            Self-Billing
          </Link>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Tab selector */}
        <div className="flex gap-2 mb-5" data-testid="model-tabs">
          {TABS.map((t) => (
            <button
              key={t.title}
              onClick={() => setActiveTab(t.title)}
              className={`px-4 py-2 rounded-full text-sm transition border ${
                activeTab === t.title
                  ? "bg-[#C5A059] text-black border-[#C5A059]"
                  : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
              data-testid={`tab-${t.model}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-[360px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search driver or phone…"
              className="w-full bg-black/40 border border-white/10 pl-9 pr-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60"
              data-testid="search-driver"
            />
          </div>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-black/40 border border-white/10 px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60"
            data-testid="period-filter"
          >
            <option value="">All periods</option>
            {periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="ml-auto text-xs text-zinc-500" data-testid="row-count">
            {filtered.length} of {rows.length} row{rows.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/5 overflow-hidden bg-[#0a0a0a]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/60">
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/5">
                  <th className="px-3 py-3 w-8"></th>
                  <th className="px-3 py-3">Period</th>
                  <th className="px-3 py-3">Driver</th>
                  <th className="px-3 py-3 text-right">Total turnover (P)</th>
                  <th className="px-3 py-3 text-right">Gross (Q)</th>
                  <th className="px-3 py-3 text-right">VAT (R)</th>
                  <th className="px-3 py-3 text-right">Company comm. (S)</th>
                  <th className="px-3 py-3 text-right">Contrib. (T)</th>
                  <th className="px-3 py-3 text-right">Cash (U)</th>
                  <th className="px-3 py-3 text-right">Brought (V)</th>
                  <th className="px-3 py-3 text-right text-[#C5A059]">Payout (W)</th>
                  <th className="px-3 py-3 text-right w-16">PDF</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="text-center py-14 text-zinc-500" data-testid="loading">
                    Loading from Google Sheets…
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-14 text-zinc-500" data-testid="empty-state">
                    No rows to display. Add data to the sheet, then click <span className="text-[#C5A059]">Refresh from Sheets</span>.
                  </td></tr>
                ) : filtered.map((r, i) => (
                  <RowRender key={`${r.sheet_row}-${i}`} r={r} tab={activeTab} isOpen={!!expanded[i]} onToggle={() => toggle(i)} />
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t border-white/10 bg-black/40 text-sm font-medium">
                    <td colSpan={3} className="px-3 py-3 text-zinc-400 uppercase text-[10px] tracking-[0.25em]">Totals</td>
                    <td className="px-3 py-3 text-right">{eur(totals.total_turnover)}</td>
                    <td className="px-3 py-3 text-right">{eur(totals.gross_turnover)}</td>
                    <td className="px-3 py-3 text-right">{eur(totals.vat)}</td>
                    <td className="px-3 py-3 text-right">{eur(totals.company_commission)}</td>
                    <td className="px-3 py-3 text-right">{eur(totals.contributions)}</td>
                    <td className="px-3 py-3 text-right">{eur(totals.total_cash)}</td>
                    <td className="px-3 py-3 text-right">{eur(totals.brought_cash)}</td>
                    <td className="px-3 py-3 text-right text-[#C5A059] font-semibold" data-testid="total-payout">
                      {eur(totals.final_payout)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="mt-4 text-[11px] text-zinc-600 leading-relaxed">
          All figures are read directly from the master Google Sheet — the backend performs
          no calculations. If a number looks wrong, edit the sheet and press
          <span className="text-[#C5A059] mx-1">Refresh from Sheets</span>.
        </div>

        <WeeklyArchive />
      </div>
    </div>
  );
}

function WeeklyArchive() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverQ, setDriverQ] = useState("");
  const [open, setOpen] = useState({});

  const loadArchive = useCallback(async (driver) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts/sheets/archive", {
        params: driver ? { driver } : {},
      });
      setWeeks(data.weeks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Arhivu nije moguće učitati.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadArchive(""); }, [loadArchive]);

  useEffect(() => {
    const t = setTimeout(() => loadArchive(driverQ.trim()), 400);
    return () => clearTimeout(t);
  }, [driverQ, loadArchive]);

  return (
    <div className="mt-10" data-testid="weekly-archive">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-light tracking-wide text-white">Arhiva tjedana</h2>
          <p className="text-[11px] text-zinc-600 mt-0.5">
            Trajna povijest — svaki tjedan se automatski arhivira u bazu i ostaje sačuvan
            i nakon što se Google tablica zamijeni novim tjednom.
          </p>
        </div>
        <input
          value={driverQ}
          onChange={(e) => setDriverQ(e.target.value)}
          placeholder="Filtriraj po vozaču…"
          className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm w-56 focus:outline-none focus:border-[#C5A059]/60"
          data-testid="archive-driver-filter"
        />
      </div>

      {loading ? (
        <div className="py-10 text-center text-zinc-500 text-sm">Učitavanje arhive…</div>
      ) : weeks.length === 0 ? (
        <div className="py-10 text-center text-zinc-500 text-sm">Nema arhiviranih tjedana.</div>
      ) : (
        <div className="space-y-3">
          {weeks.map((w) => (
            <div key={w.period} className="rounded-2xl border border-white/5 bg-[#0a0a0a] overflow-hidden"
                 data-testid={`archive-week-${w.iso_week || w.period}`}>
              <button
                onClick={() => setOpen((o) => ({ ...o, [w.period]: !o[w.period] }))}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition text-left"
                data-testid={`archive-week-toggle-${w.iso_week || w.period}`}
              >
                <div>
                  <p className="text-white font-medium">{w.period} <span className="text-zinc-600 text-xs ml-2">{w.iso_week || ""}</span></p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{w.totals.drivers} vozača</p>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-zinc-600">Promet</p>
                    <p className="text-sm text-zinc-200">{eur(w.totals.turnover)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-zinc-600">Za isplatu</p>
                    <p className="text-sm text-[#C5A059] font-semibold">{eur(w.totals.payout)}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open[w.period] ? "rotate-180" : ""}`} />
                </div>
              </button>
              {open[w.period] && (
                <div className="border-t border-white/5 px-5 py-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[9px] uppercase tracking-widest text-zinc-600 text-left">
                        <th className="py-1.5">Vozač</th>
                        <th className="py-1.5">Model</th>
                        <th className="py-1.5 text-right">Promet</th>
                        <th className="py-1.5 text-right">Za isplatu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.drivers.map((d, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="py-2 text-zinc-200">{d.driver_name}</td>
                          <td className="py-2 text-zinc-500 text-xs">{d.tab || d.model || "—"}</td>
                          <td className="py-2 text-right text-zinc-300">{eur(d.total_turnover)}</td>
                          <td className="py-2 text-right text-[#C5A059]">{eur(d.final_payout)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RowRender({ r, tab, isOpen, onToggle }) {
  const [b2b, setB2b] = useState(null);
  useEffect(() => {
    if (!isOpen || b2b) return;
    api.get("/admin/payouts/sheets/b2b", { params: { driver: r.driver_name, period: r.period } })
      .then(({ data }) => setB2b(data))
      .catch(() => setB2b({ has_any: false }));
  }, [isOpen, b2b, r.driver_name, r.period]);
  const openPdf = async () => {
    try {
      const token = localStorage.getItem("juke_admin_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}/api/admin/payouts/sheets/pdf`
        + `?tab=${encodeURIComponent(tab)}`
        + `&driver=${encodeURIComponent(r.driver_name)}`
        + `&period=${encodeURIComponent(r.period)}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("PDF fetch failed");
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objUrl), 30_000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[payout-pdf]", e);
    }
  };
  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`payout-row-${r.sheet_row}`}>
        <td className="px-3 py-3">
          <button
            onClick={onToggle}
            className="text-zinc-500 hover:text-white transition"
            data-testid={`toggle-row-${r.sheet_row}`}
          >
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-3 py-3 text-zinc-300 whitespace-nowrap">{r.period}</td>
        <td className="px-3 py-3 text-white whitespace-nowrap">
          {r.driver_name}
          {r.phone && <div className="text-[11px] text-zinc-500">+{r.phone}</div>}
        </td>
        <td className="px-3 py-3 text-right text-zinc-300">{eur(r.total_turnover)}</td>
        <td className="px-3 py-3 text-right text-zinc-300">{eur(r.gross_turnover)}</td>
        <td className="px-3 py-3 text-right text-zinc-400">{eur(r.vat)}</td>
        <td className="px-3 py-3 text-right text-zinc-400">{eur(r.company_commission)}</td>
        <td className="px-3 py-3 text-right text-zinc-400">{eur(r.contributions)}</td>
        <td className="px-3 py-3 text-right text-zinc-300">{eur(r.total_cash)}</td>
        <td className="px-3 py-3 text-right text-zinc-400">{eur(r.brought_cash)}</td>
        <td className="px-3 py-3 text-right text-[#C5A059] font-semibold">{eur(r.final_payout)}</td>
        <td className="px-3 py-3 text-right">
          <button
            onClick={openPdf}
            className="p-1.5 rounded-full border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059] text-zinc-400 transition"
            title="Download payout PDF"
            data-testid={`payout-pdf-${r.sheet_row}`}
          >
            <FileDown className="w-4 h-4" />
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-b border-white/10 bg-black/40">
          <td colSpan={12} className="px-6 py-4">
            <div className="grid grid-cols-2 gap-8">
              <SubBlock title="Uber" rows={UBER_COLS.map(([k, l]) => [l, r[k]])} />
              <SubBlock title="Bolt" rows={BOLT_COLS.map(([k, l]) => [l, r[k]])} />
            </div>
            {b2b?.direct?.has_any && (
              <div className="mt-5 pt-4 border-t border-white/5" data-testid={`payout-direct-${r.sheet_row}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-2">
                  Direktne privatne vožnje (neto osnovica)
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    <tr>
                      <td className="py-1 text-zinc-500">Bruto promet · {b2b.direct.count} vožnji</td>
                      <td className="py-1 text-right text-zinc-200">{eur(b2b.direct.gross)}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-zinc-500">Neto osnovica{b2b.direct.vat > 0 ? ` (PDV ${eur(b2b.direct.vat)})` : ""}</td>
                      <td className="py-1 text-right text-zinc-200">{eur(b2b.direct.net_base)}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-zinc-500">Udio vozača / udio tvrtke</td>
                      <td className="py-1 text-right text-zinc-200">{eur(b2b.direct.driver_share)} / {eur(b2b.direct.company_share)}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-zinc-500">− Zadržana gotovina u vozilu</td>
                      <td className="py-1 text-right text-zinc-200">− {eur(b2b.direct.cash_retained)}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-[#C5A059]">Neto za isplatu (direktne)</td>
                      <td className="py-1 text-right text-[#C5A059] font-medium">{eur(b2b.direct.net_driver_payout)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {b2b?.has_any && (
              <div className="mt-5 pt-4 border-t border-white/5" data-testid={`payout-b2b-${r.sheet_row}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 mb-2">
                  B2B Vožnje (Mjesečni R-1 račun)
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    <tr>
                      <td className="py-1 text-zinc-500">Na mjesečni račun · {b2b.invoice_count} vožnji (u bruto prometu)</td>
                      <td className="py-1 text-right text-zinc-200">{eur(b2b.invoice_turnover)}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-zinc-500">Naplaćeno od gosta u vozilu · {b2b.collect_count} vožnji</td>
                      <td className="py-1 text-right text-zinc-200">{eur(b2b.collect_amount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function SubBlock({ title, rows }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-2">{title}</div>
      <table className="w-full text-xs">
        <tbody>
          {rows.map(([label, val]) => (
            <tr key={label}>
              <td className="py-1 text-zinc-500">{label}</td>
              <td className="py-1 text-right text-zinc-200">{eur(val)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
